import { Alert, Stack, Typography } from '@mui/material';
import { BulkOperationError, VisitorRole } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import ModalTemplate from 'v2/components/Modal/ModalTemplate';
import useModalContext from 'v2/components/Modal/useModalContext';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useBulkUserActions from 'v2/hooks/useBulkUserActions/useBulkUserActions';
import RolesSection, { RolesFormValues } from 'v2/pages/Users/Drawers/UserFormDrawer/RolesSection';
import { getSelectedUserDescription } from '../components/selectedUsersUtils';
import { SelectedUser } from './BulkUserActionFailuresModal/bulkUserActionErrorsUtils';
import BulkUserActionFailuresModal from './BulkUserActionFailuresModal/BulkUserActionFailuresModal';

const FORM_ID = 'assign-roles-form';

interface Props {
  selectedUsers: SelectedUser[];
  hasKeySharingSelectedUsers: boolean;
  onSuccess: () => void;
}

const AssignRolesModal: React.FC<Props> = ({ selectedUsers, hasKeySharingSelectedUsers, onSuccess }) => {
  const { translation } = useTranslation();
  const { closeModal } = useModalContext();
  const { showSnackbar } = useSnackbarContext();
  const [errors, setErrors] = useState<BulkOperationError[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [submittedRoles, setSubmittedRoles] = useState<VisitorRole[]>([]);
  const { bulkAssignVisitorRoles, bulkAssignVisitorRolesLoading } = useBulkUserActions({
    translation,
    handleFetchError: setErrorMessage
  });
  const {
    clearErrors,
    control,
    handleSubmit,
    formState: { errors: formErrors, isSubmitting }
  } = useForm<RolesFormValues>({
    mode: 'onTouched',
    defaultValues: { isAdministratorRole: false, isInstallerRole: false }
  });

  const handleSuccess = () => {
    showSnackbar(translation.users_assigned_roles, { severity: 'success' });
    onSuccess();
    closeModal();
  };

  const handleResult = (payload: { errors: BulkOperationError[] }) => {
    if (payload.errors.length > 0) {
      setErrors(payload.errors);
      return;
    }

    handleSuccess();
  };

  const onSubmit = async ({ isAdministratorRole, isInstallerRole }: RolesFormValues) => {
    const roles = [
      ...(isAdministratorRole ? [VisitorRole.Admin] : []),
      ...(isInstallerRole ? [VisitorRole.Installer] : [])
    ];
    setSubmittedRoles(roles);
    setErrorMessage(undefined);

    const payload = await bulkAssignVisitorRoles({ visitorIds: selectedUsers.map(({ id }) => id), roles });

    if (payload) handleResult(payload);
  };

  const handleRetry = async (failedVisitorIds: string[]) => {
    setErrorMessage(undefined);
    const payload = await bulkAssignVisitorRoles({ visitorIds: failedVisitorIds, roles: submittedRoles });

    if (payload) handleResult(payload);
  };

  if (errors.length > 0) {
    return (
      <BulkUserActionFailuresModal
        errors={errors}
        selectedUsers={selectedUsers}
        title={translation.bulk_action_failures_title}
        loading={bulkAssignVisitorRolesLoading}
        errorMessage={errorMessage}
        onRetry={handleRetry}
      />
    );
  }

  return (
    <ModalTemplate
      title={translation.assign_roles}
      primaryAction={{
        id: 'apply-assign-roles-button',
        label: translation.apply,
        form: FORM_ID,
        loading: isSubmitting || bulkAssignVisitorRolesLoading,
        disabled: isSubmitting || bulkAssignVisitorRolesLoading
      }}
      secondaryAction={{ id: 'cancel-assign-roles-button', label: translation.cancel }}
    >
      <Stack component="form" id={FORM_ID} onSubmit={handleSubmit(onSubmit)} noValidate gap={3}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        <Stack gap={0}>
          <Typography variant="body2">
            {getSelectedUserDescription(selectedUsers.length, translation.selected_users_assign_roles_description)}
          </Typography>
          {hasKeySharingSelectedUsers && (
            <Typography variant="body2">{translation.key_sharing_roles_warning}</Typography>
          )}
        </Stack>
        <RolesSection
          control={control}
          selectAtLeastOneRoleValidationMessage={translation.select_at_least_one_role}
          selectAtLeastOneRoleError={formErrors.isAdministratorRole?.message}
          onRoleChange={() => clearErrors('isAdministratorRole')}
        />
      </Stack>
    </ModalTemplate>
  );
};

export default AssignRolesModal;
